import React, { Suspense } from 'react';
import { lazyWithProbe } from '@/main/services/lazyWithProbe';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';
import useAsyncModalConfirm from '@/main/components/shared/asyncModalConfirm/asyncModalConfirm';
import KOGStepper from '@/main/components/shared/stepper/stepper';
import { SpinnerLoading } from '@/main/components/shared/styles';
import { RoutesConstants } from '@/main/routes/routesConstants';
import {
  getGoogleRecaptchaPublicKey,
  getTranslateByKey
} from '@/main/services/utils';

import RegisterStepsProvider, { useRegisterSteps } from './context';
import { TypeRegisterStep, TypeRegisterStepScreen } from './types';
import {
  STEPS_NOT_SHOW_MODAL_BACK_STEP,
  STEPS_NOT_SHOW_MODAL_EXIT_REGISTRATION,
  STEPS_NOT_SHOW_STEPPER
} from './utilsSteps';
import * as S from './styles';

const AddressData = lazyWithProbe(() =>
  import('./steps/addressData/addressData')
);
const CompanyData = lazyWithProbe(() =>
  import('./steps/companyData/companyData')
);
const Contact = lazyWithProbe(() => import('./steps/contact/contact'));
const DistributionRegion = lazyWithProbe(() =>
  import('./steps/distributionRegion/distributionRegion')
);
const Insurers = lazyWithProbe(() => import('./steps/insurers/insurers'));
const LegalInformation = lazyWithProbe(() =>
  import('./steps/legalInformation/legalInformation')
);
const Summary = lazyWithProbe(() => import('./steps/summary/summary'));
const SupplyConfiguration = lazyWithProbe(() =>
  import('./steps/supplyConfiguration/supplyConfiguration')
);
const Warehouse = lazyWithProbe(() =>
  import('./steps/warehouseData/warehouseData')
);
const Finish = lazyWithProbe(() => import('./steps/finish/finish'));
interface IMapSteps {
  component: React.LazyExoticComponent<React.FC>;
  translateTitleStep: string;
}
const MAP_STEPS_NAME: Record<TypeRegisterStep, IMapSteps> = {
  Address: {
    component: AddressData,
    translateTitleStep: 'information.addressData'
  },
  CompanyData: {
    component: CompanyData,
    translateTitleStep: 'information.companyData'
  },
  Contact: {
    component: Contact,
    translateTitleStep: 'information.contact'
  },
  DistributionRegion: {
    component: DistributionRegion,
    translateTitleStep: 'information.distributionInfo'
  },
  Insurers: {
    component: Insurers,
    translateTitleStep: 'information.insurers'
  },
  LegalInformation: {
    component: LegalInformation,
    translateTitleStep: 'information.legalInformation'
  },
  Summary: {
    component: Summary,
    translateTitleStep: 'information.summary'
  },
  SupplyConfiguration: {
    component: SupplyConfiguration,
    translateTitleStep: 'supplyConfiguration.supplyConfiguration'
  },
  Warehouse: {
    component: Warehouse,
    translateTitleStep: 'information.warehouseData'
  },
  Finish: {
    component: Finish,
    translateTitleStep: ''
  }
};
const MAP_TITLE_SCREEN_PAGE: Record<TypeRegisterStepScreen, string> = {
  bodyshop: 'information.bodyshopRegister',
  supplier: 'information.supplierRegister'
};

interface IRegisterStepsProps {
  typeRegister: TypeRegisterStepScreen;
  orderStepsToRender: TypeRegisterStep[];
}
function RegisterSteps({ typeRegister }: IRegisterStepsProps) {
  const navigate = useNavigate();
  const {
    currentStep,
    availableSteps,
    completedSteps,
    isDirtyForm,
    isLoadingFormRootStep,
    isLoadingSaveStep,
    navigateToStep
  } = useRegisterSteps();
  const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();

  const StepComponent = MAP_STEPS_NAME[currentStep].component;
  const translatesSteps: Record<TypeRegisterStep, string> = Object.entries(
    MAP_STEPS_NAME
  ).reduce((acc, [key, value]) => {
    acc[key as TypeRegisterStep] =
      value.translateTitleStep !== ''
        ? getTranslateByKey(value.translateTitleStep)
        : '';
    return acc;
  }, {} as Record<TypeRegisterStep, string>);
  const translatesPage = getTranslateByKey(MAP_TITLE_SCREEN_PAGE[typeRegister]);
  const stepsToShowStepper: TypeRegisterStep[] = availableSteps.filter(
    step => !STEPS_NOT_SHOW_STEPPER.includes(step)
  );

  function getStepNameTranslated() {
    if (typeRegister === 'bodyshop' && currentStep === 'Address') {
      return getTranslateByKey('information.stepDeliveryAddressData');
    }

    return translatesSteps[currentStep];
  }

  async function handleClickLogo(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    e.stopPropagation();

    if (STEPS_NOT_SHOW_MODAL_EXIT_REGISTRATION.includes(currentStep)) {
      navigate(RoutesConstants.home);
      return;
    }

    const messageModal = `${getTranslateByKey(
      'information.backTologinScreen'
    )} ${isDirtyForm ? getTranslateByKey('information.dataDiscardbacklogin') : ''
      }`;
    const status = await getConfirmation(messageModal);

    if (status) {
      navigate(RoutesConstants.home);
    }
  }

  async function handleClickStep(step: TypeRegisterStep) {
    let status = true;

    if (isDirtyForm && !STEPS_NOT_SHOW_MODAL_BACK_STEP.includes(currentStep)) {
      const messageModal = `${getTranslateByKey('information.backTostep')} ${isDirtyForm ? getTranslateByKey('information.dataDiscardbacklogin') : ''
        }`;
      status = await getConfirmation(messageModal);
    }

    if (status) {
      navigateToStep(step as TypeRegisterStep);
    }
  }
  return (
    <>
      <S.Container>
        <S.Header>
          <S.Logo to={RoutesConstants.home} onClick={handleClickLogo} />
          <S.AreaHeaderSteps>
            <S.TitlePage>{translatesPage}</S.TitlePage>
            <S.TitleStep>{getStepNameTranslated()}</S.TitleStep>
          </S.AreaHeaderSteps>
          {STEPS_NOT_SHOW_STEPPER.includes(currentStep) ? null : (
            <KOGStepper
              isLoading={isLoadingFormRootStep || isLoadingSaveStep}
              currentStep={currentStep}
              completedSteps={completedSteps}
              steps={stepsToShowStepper}
              titleSteps={translatesSteps}
              onClickStep={step => handleClickStep(step as TypeRegisterStep)}
              typeRegister={typeRegister}
            />
          )}
        </S.Header>
        <S.Content>
          <Suspense fallback={<SpinnerLoading />}>
            <StepComponent />
          </Suspense>
        </S.Content>
        <S.Footer></S.Footer>
        <S.ContainerStyle>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 4">
            <path d="M 0 0 L 3 0 C 1 0 2 4 0 4" />
          </svg>
        </S.ContainerStyle>
        <ModalConfirmation />
      </S.Container>
    </>
  );
}
function RegisterStepsWrapper(props: IRegisterStepsProps) {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={getGoogleRecaptchaPublicKey()}>
      <RegisterStepsProvider
        orderStepsToRender={props.orderStepsToRender}
        typeRegister={props.typeRegister}>
        <RegisterSteps {...props} />
      </RegisterStepsProvider>
    </GoogleReCaptchaProvider>
  );
}

export default RegisterStepsWrapper;
